import React, { FC, ReactElement, ReactNode } from 'react';

import Head from 'next/head';
import { NextPage } from 'next';
import Router, { useRouter } from 'next/router';
import { AppProps } from 'next/app';
import nProgress from 'nprogress';
import { EmotionCache } from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { appWithTranslation, useTranslation } from 'next-i18next';
import RTL from 'components/RTL';
import MuiTheme from 'theme/MuiTheme';
import { AppProvider } from 'contexts/AppContext';
import SettingsProvider from 'contexts/SettingContext';
import SnackbarProvider from 'components/SnackbarProvider';
import createEmotionCache from 'createEmotionCache';

import { initializeApp } from 'firebase/app';
import { getPerformance } from '@firebase/performance';
import { IS_PRODUCTION } from 'config';

import 'nprogress/nprogress.css';
import 'simplebar-react/dist/simplebar.min.css';
import { AuthProvider } from 'contexts/AuthContext';
import * as FirebaseAuth from '@firebase/auth';
import axios from 'axios';

import 'assets/scss/style.scss';
import Script from 'next/script';
import { persistor, wrapper } from 'store';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { useSearchParams } from 'next/navigation';
import { Favicon } from '../src/components/Favicon';

//Binding events.
Router.events.on('routeChangeStart', () => nProgress.start());
Router.events.on('routeChangeComplete', () => nProgress.done());
Router.events.on('routeChangeError', () => nProgress.done());
// small change
nProgress.configure({ showSpinner: false });

// Firebase client configuration
const firebaseConfig: any = {
  apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
  authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
  databaseURL: process.env.NEXT_PUBLIC_FIREBASE_DATABASE_URL,
  projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
  storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID
  //measurementId: process.env.NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID
};

const isGoogleAnalyticsEnable = !!process.env.NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID;

// 상용기
if (isGoogleAnalyticsEnable) {
  firebaseConfig.measurementId = process.env.NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID;
}

// Initialize Firebase
const firebaseApp = (() => {
  try {
    if (typeof window !== 'undefined') {
      return initializeApp(firebaseConfig);
    }
  } catch (error) {
    console.error('Firebase initializeApp error', error, error.stack);
  }
})();

// Initialize Performance Monitoring and get a reference to the service
if (typeof window !== 'undefined') {
  // Initialize Performance Monitoring and get a reference to the service
  const perf = getPerformance(firebaseApp);

  if (!IS_PRODUCTION) {
    console.debug('IS_PRODUCTION = FALSE');
    perf.dataCollectionEnabled = false;
    perf.instrumentationEnabled = false;
  }
  console.debug(
    `${process.env.NODE_ENV}, ${process.env.NEXT_PUBLIC_SERVICE_PROFILE}, ${IS_PRODUCTION}, ${process.env.NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID}`
  );
  console.debug('Firebase performance initialized.');
}

declare global {
  var FIREBASE_APPCHECK_DEBUG_TOKEN: boolean | string | undefined;
}

axios.interceptors.request.use(async (config) => {
  try {
    // 클라이언트 사이드에서 요청
    if (typeof window !== 'undefined') {
      const auth = FirebaseAuth.getAuth();

      if (auth.currentUser) {
        const { accessToken, stsTokenManager }: any = auth.currentUser;
        let bearerToken = accessToken;

        // 토큰이 만료되었을 경우 갱신
        if (stsTokenManager.isExpired) {
          console.debug('[나라셀라 멤버쉽] 사용자 토큰이 만료되어 갱신합니다.');
          const { token } = await auth.currentUser.getIdTokenResult(true);
          bearerToken = token;

          console.debug(`[나라셀라 멤버쉽]  ㄴ 갱신된 토큰=${bearerToken}`);
        }

        config.headers.Authorization = `Bearer ${bearerToken}`;
      } else {
        delete config.headers.Authorization;
      }
    } else {
      // 서버 사이드에서 요청
    }
  } catch (e) {
    console.warn('axios interceptor 오류');
  }

  return config;
});

// Client-side cache, shared for the whole session of the user in the browser.
const clientSideEmotionCache = createEmotionCache();

export interface MyAppProps extends AppProps {
  emotionCache?: EmotionCache;
  Component: NextPage & { getLayout?: (page: ReactElement) => ReactNode };
}

const WineoneApp: FC<MyAppProps> = ({ Component, emotionCache = clientSideEmotionCache, ...rest }: MyAppProps) => {
  const getLayout = Component.getLayout ?? ((page) => page);

  const router = useRouter();
  const searchParams = useSearchParams();
  const { i18n, ...others } = useTranslation();

  const { locale } = router;

  React.useEffect(() => {
    i18n.changeLanguage?.(locale);
  }, [i18n, locale]);

  // 카카오 스크립트 온로드 콜백
  const onKakaoScriptLoaded = React.useCallback(() => {
    if (typeof process.env.NEXT_PUBLIC_KAKAO_APP_KEY === 'string') {
      window.Kakao.init(process.env.NEXT_PUBLIC_KAKAO_APP_KEY);
      console.debug('[카카오 Auth] 스크립트 로드됨');
    } else {
      console.warn('[카카오 Auth] 카카오 스크립트트 세팅 오류 [KAKAO_APP_KEY not exists]');
    }
  }, []);

  // Affiliates: 사용자 또는 입점샵 코드로 들어왔을 경우
  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      const shareType = searchParams.get('shr_type');

      if (shareType === 'join_ms_no') {
        try {
          const shareCode = searchParams.get('join_ms_no');
          if (shareCode) {
            // 24시간 유효
            const exp: number = Date.now() + 1000 * 60 * 60 * 24;
            window.localStorage.setItem('shr', JSON.stringify({ type: shareType, code: shareCode, exp }));

            // 주소창 URL 마스킹
            const maskedSearchParams = new URLSearchParams(window.location.search);
            maskedSearchParams.delete('shr_type');
            maskedSearchParams.delete('shr_code');
            maskedSearchParams.delete('join_ms_no');
            const params = maskedSearchParams.toString();

            const asPath = `${window.location.pathname}${params ? `?${params}` : ''}${window.location.hash}`;

            // URL 주소 마스킹
            router.replace(window.location.href, asPath, { shallow: true });
          }
        } catch (e) {
          console.error('[Affiliates] shr_code 저장실패', e);
        }
      }
    }
  }, []);

  const { store, props } = wrapper.useWrappedStore(rest);

  // render
  return (
    <CacheProvider value={emotionCache}>
      {/* Site verification meta */}
      {IS_PRODUCTION && (
        <Head>
          <meta name="google-site-verification" content="-J34l0ApN1V-ZsmPFGQWjVLA1S1D1RbrN01pKriYGBY" />
          <meta name="naver-site-verification" content="4693ddd9cd1b58de0b8e01cb53af57f767833168" />
        </Head>
      )}
      <Head>
        <meta charSet="utf-8" />
        <link rel="canonical" href="https://www.naracellar.com/" key="canonical" />
        <meta name="description" content="나라셀라 통합 멤버십" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />
        <meta name="revisit-after" content="1 days" />
        <meta name="author" content="NARACELLAR" />
        <meta name="copyright" content={`NARACELLAR INC. ${new Date().getFullYear()} ALL RIGHTS RESERVED.`} />
        <title>{`나라셀라 통합 멤버십 | ${process.env.NEXT_PUBLIC_SITENAME}`}</title>
        <Favicon />
      </Head>

      <Script type="text/javascript" charSet="UTF-8" src="//t1.daumcdn.net/kas/static/kp.js" />

      <Script
        src="//t1.kakaocdn.net/kakao_js_sdk/2.4.0/kakao.min.js"
        integrity="sha384-mXVrIX2T/Kszp6Z0aEWaA8Nm7J6/ZeWXbL8UpGRjKwWe56Srd/iyNmWMBhcItAjH"
        crossOrigin="anonymous"
        onLoad={onKakaoScriptLoaded}
      />

      <Script
        src={`//dapi.kakao.com/v2/maps/sdk.js?appkey=${process.env.NEXT_PUBLIC_KAKAO_APP_KEY}&libraries=services,clusterer&autoload=false`}
      />

      <Provider store={store}>
        <PersistGate persistor={persistor} loading={null}>
          <MuiTheme>
            <AuthProvider>
              <AppProvider>
                <SettingsProvider>
                  <SnackbarProvider>
                    <RTL>{getLayout(<Component {...props.pageProps} />)}</RTL>
                  </SnackbarProvider>
                </SettingsProvider>
              </AppProvider>
            </AuthProvider>
          </MuiTheme>
        </PersistGate>
      </Provider>
    </CacheProvider>
  );
};

if (typeof window !== 'undefined') {
  const vh = window.innerHeight * 0.01;
  document.documentElement.style.setProperty('--vh', `${vh}px`);
  window.addEventListener('resize', () => {
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty('--vh', `${vh}px`);
  });
}

// export default wrapper.withRedux(appWithTranslation(WineoneApp));
export default appWithTranslation(WineoneApp);
